/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cn } from "@/shared/ui/utils/tailwind"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

/**
 * Dialog overlay with backdrop blur and fade animation.
 *
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>} props - Overlay properties.
 * @returns {JSX.Element} Rendered overlay.
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[10000] bg-(--overlay-backdrop) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Dialog content panel centered on screen with overlay.
 *
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>} props - Content properties.
 * @returns {JSX.Element} Rendered dialog content.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[10000] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-(--border-subtle) bg-(--surface-raised) p-6 shadow-(--shadow-lg) duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-(--radius-2xl)",
        /*
         * A bottom sheet on a phone.
         *
         * A box centred in the viewport is a desktop shape: it arrives from
         * nowhere, and the hand that has to reach it is at the other end of
         * the screen. Anchored to the bottom it rises from the edge the
         * thumb is already on, and the rounded top with the grab handle
         * above says which way it came from and which way it leaves.
         *
         * Every override here undoes one of the centring classes above
         * rather than sitting beside it, so the two cannot both apply.
         */
        "max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:max-h-[88svh] max-md:w-full max-md:max-w-none",
        "max-md:translate-x-0 max-md:translate-y-0 max-md:overflow-y-auto",
        "max-md:rounded-b-none max-md:rounded-t-[14px] max-md:border-b-0 max-md:pt-(--space-3)",
        "max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom",
        "max-md:data-[state=open]:slide-in-from-left-0 max-md:data-[state=closed]:slide-out-to-left-0",
        "max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100",
        className
      )}
      {...props}
    >
      {/* The handle that says this came up from the edge and can go back. */}
      <span
        aria-hidden="true"
        data-testid="dialog-grabber"
        className="mx-auto hidden h-1 w-10 shrink-0 rounded-full bg-(--border-subtle) max-md:block"
      />
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * Dialog header layout container.
 *
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Header properties.
 * @returns {JSX.Element} Rendered header.
 */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

/**
 * Dialog footer layout container.
 *
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Footer properties.
 * @returns {JSX.Element} Rendered footer.
 */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

/**
 * Dialog title element.
 *
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>} props - Title properties.
 * @returns {JSX.Element} Rendered title.
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)]',
      'leading-[var(--leading-tight)] tracking-[var(--tracking-tight)]',
      'text-(--text-primary) font-[family-name:var(--font-family)]',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/**
 * Dialog description text.
 *
 * @param {React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>} props - Description properties.
 * @returns {JSX.Element} Rendered description.
 */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPrimitive,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
